import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

const ProjectContext = createContext();
export const useProjects = () => useContext(ProjectContext);

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [clients, setClients] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);

  const getToken = () => localStorage.getItem("token") || "";

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Fetch Projects
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError("");

      const token = getToken();
      if (!token) return;

      const res = await fetch(`${BASE_URL}/projects`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch projects");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      setProjects(data.data || []);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Error fetching projects");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Clients
  const fetchClients = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const res = await fetch(`${BASE_URL}/clients`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) return;

      const data = await parseJson(res);

      if (data) {
        setClients(data.data || []);
      }
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  // Fetch Project Types
  const fetchProjectTypes = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const res = await fetch(`${BASE_URL}/project-types`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) return;

      const data = await parseJson(res);

      if (data) {
        setProjectTypes(data.data || []);
      }
    } catch (err) {
      console.error("Error fetching project types:", err);
    }
  };

  // Create Project
  const addProject = async (payload) => {
    try {
      const token = getToken();
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      payload.create_audit_id =
        payload.create_audit_id || user?.id || null;

      const res = await fetch(`${BASE_URL}/projects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to create project",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 201) {
        await fetchProjects();

        toast.success(data.message || "Project created successfully");
      }

      return data;
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: err.message || "Error creating project",
      };
    }
  };

  // Update Project
  const updateProject = async (id, editModal) => {
    const payload = {
      project_name: editModal.project_name,
      client_id: editModal.client_id,
      project_type_id: editModal.project_type_id,
      start_date: editModal.start_date,
      end_date: editModal.end_date,
      status: editModal.status,
      description: editModal.description,
      budget: editModal.budget,
    };

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    payload.create_audit_id =
      payload.create_audit_id || user?.id || null;

    try {
      const res = await fetch(`${BASE_URL}/projects/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update project",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchProjects();

        toast.success(data.message || "Project updated successfully");
      }

      return data;
    } catch (err) {
      console.error("Error updating project:", err);

      return {
        success: false,
        message: err.message || "Error updating project",
      };
    }
  };

  // Delete Project
  const deleteProject = async (id) => {
    try {
      const confirmDelete = window.confirm(
        "🗑️ Are you sure you want to delete this project?"
      );

      if (!confirmDelete) return false;

      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${BASE_URL}/projects/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          delete_audit_id: user?.id,
        }),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Delete failed",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        setProjects((prev) => prev.filter((p) => p.id !== id));

        toast.success(data.message || "Project deleted successfully");

        return {
          success: true,
          message: data.message,
        };
      }

      return {
        success: false,
        message: data.message || "Delete failed",
      };
    } catch (err) {
      console.error("Error deleting project:", err);

      return {
        success: false,
        message: err.message || "Error deleting project",
      };
    }
  };

  // Assign Employees
  const assignEmployeesToProject = async (projectId, userIds) => {
    try {
      const token = getToken();
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const payload = {
        project_id: projectId,
        user_ids: userIds,
        create_audit_id: user?.id || null,
      };

      const res = await fetch(`${BASE_URL}/projects/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to assign employees",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchProjects();

        toast.success(data.message || "Employees assigned successfully");
      }

      return data;
    } catch (err) {
      console.error("Error assigning employees:", err);

      return {
        success: false,
        message: err.message || "Error assigning employees",
      };
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchClients();
    fetchProjectTypes();
  }, []);

  return (
    <ProjectContext.Provider
      value={{
        projects,
        loading,
        error,
        clients,
        fetchProjects,
        projectTypes,
        addProject,
        updateProject,
        deleteProject,
        assignEmployeesToProject,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};